import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Loader2, Package, Receipt, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { planCatalogAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const TIERS = ['starter', 'professional', 'enterprise'];

const money = (amount, currency = 'USD') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const PlanCatalogModule = () => {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [invoiceNote, setInvoiceNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // One failure should not blank the page — each card degrades on its own.
      const [meRes, plansRes, addOnsRes, billingRes] = await Promise.allSettled([
        planCatalogAPI.mine(),
        planCatalogAPI.listPlans(),
        planCatalogAPI.listAddOns(),
        planCatalogAPI.billingEstimate(),
      ]);
      if (meRes.status === 'fulfilled') setMe(meRes.value?.data || null);
      if (plansRes.status === 'fulfilled') setPlans(plansRes.value?.data?.plans || []);
      if (addOnsRes.status === 'fulfilled') setAddOns(addOnsRes.value?.data?.add_ons || []);
      if (billingRes.status === 'fulfilled') {
        setInvoice(billingRes.value?.data?.invoice || null);
        setInvoiceNote(billingRes.value?.data?.note || '');
      }
      if (meRes.status === 'rejected') {
        toast.error(getErrorMessage(meRes.reason, 'Failed to load your plan'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runUpgradeEstimate = async (tier) => {
    try {
      setEstimating(tier);
      const res = await planCatalogAPI.upgradeEstimate(tier);
      setEstimate(res?.data || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not estimate the upgrade'));
    } finally {
      setEstimating('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plan…
      </div>
    );
  }

  const currency = invoice?.currency || 'USD';
  const availableFeatures = new Set(me?.available_features || []);

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Package className="h-4 w-4" /> Current plan
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-4 sm:grid-cols-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Tier</p>
              <p className="text-foreground font-medium capitalize">{me?.tier || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Vertical</p>
              <p className="text-foreground font-medium capitalize">{me?.vertical || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Licensed seats</p>
              <p className="text-foreground font-medium">{me?.licensed_user_count ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Features enabled</p>
              <p className="text-foreground font-medium">{me?.available_feature_count ?? 0}</p>
            </div>
          </div>
          {(me?.active_add_ons || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {me.active_add_ons.map((key) => (
                <Badge key={key} className="bg-primary/10 text-primary">{key}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Receipt className="h-4 w-4" /> This month, estimated
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Advisory only — computed from your plan and seat count, never charged from here.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {!invoice ? (
            <p className="text-sm text-muted-foreground">{invoiceNote || 'No estimate available for this plan.'}</p>
          ) : (
            <div className="space-y-2">
              {(invoice.line_items || []).map((li, idx) => (
                <div key={`${li.metric_type}-${idx}`} className="flex items-center justify-between gap-3 text-sm border-b border-border/40 pb-2 last:border-0">
                  <div className="min-w-0">
                    <p className="text-foreground truncate">{li.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {li.quantity} × {money(li.unit_price, currency)}
                    </p>
                  </div>
                  <p className="text-foreground font-medium shrink-0">{money(li.amount, currency)}</p>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">{money(invoice.subtotal, currency)}</span>
              </div>
              <div className="flex items-center justify-between text-base font-semibold">
                <span className="text-foreground">Total</span>
                <span className="text-foreground">{money(invoice.total_amount, currency)}</span>
              </div>
              {invoice.requires_quote && (
                <p className="text-xs text-amber-600 pt-1">Some lines need a quote — talk to sales for a firm number.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Plans for your vertical
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {plans.length === 0 ? (
            <p className="text-sm text-muted-foreground">No catalog plans published for this vertical.</p>
          ) : plans.map((plan) => (
            <div key={plan.id} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{plan.name}</p>
                    {plan.tier === me?.tier && <Badge className="bg-green-500/15 text-green-600">current</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {plan.price_per_user_month
                      ? `${money(plan.price_per_user_month, plan.currency)} / user / month`
                      : plan.price_fixed_monthly
                        ? `${money(plan.price_fixed_monthly, plan.currency)} / month`
                        : 'Custom pricing'}
                    {plan.max_users ? ` · up to ${plan.max_users} users` : ' · unlimited users'}
                  </p>
                </div>
                {plan.tier !== me?.tier && TIERS.includes(plan.tier) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runUpgradeEstimate(plan.tier)}
                    disabled={estimating === plan.tier}
                  >
                    {estimating === plan.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Estimate'}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {estimate && (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
              <p className="text-foreground">
                <span className="capitalize">{estimate.current_tier}</span> →{' '}
                <span className="capitalize font-medium">{estimate.target_tier}</span>
                {estimate.target_plan_name ? ` (${estimate.target_plan_name})` : ''}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {estimate.estimated_monthly_delta == null
                  ? 'Custom tier — pricing needs a quote.'
                  : `Estimated change: ${money(estimate.estimated_monthly_delta, currency)} / month`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Add-ons</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {addOns.length === 0 ? (
            <p className="text-sm text-muted-foreground">No add-ons available on this plan.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {addOns.map((a) => (
                <div key={a.key} className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2">
                    {a.active
                      ? <Check className="h-4 w-4 text-green-600" />
                      : <X className="h-4 w-4 text-muted-foreground" />}
                    <p className="text-sm font-medium text-foreground">{a.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.base_price ? `${money(a.base_price, currency)} / month` : ''}
                    {a.per_unit_price ? ` · ${money(a.per_unit_price, currency)} / unit` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {availableFeatures.size > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border/50 bg-muted/20">
            <CardTitle className="text-foreground text-base">Entitlements</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-1.5">
              {[...availableFeatures].sort().map((key) => (
                <Badge key={key} className="bg-muted text-muted-foreground font-normal">{key}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PlanCatalogModule;
